import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

/**
 * The stage carousel is what makes the projected /stage view legible when
 * nobody is there to scroll: it paces phases on a timer instead of stacking
 * everything in one scroll container.
 *
 * Phase model (Stage Calm, post-collapse):
 * - clusters phase: shows the full cluster grid for the question (2x2, 2x3,
 *   or 2x4 depending on event.max_clusters). All clusters fit on one screen.
 *   No pagination, no carousel page-flipping.
 *
 * Insights are NO LONGER carousel phases. They surface as full-screen
 * interruption moments triggered by realtime ai_insights INSERT events
 * (handled in StageView, not here).
 *
 * Pause is driven by two independent inputs:
 * - events.carousel_paused (DB, facilitator-controlled, persistent)
 * - document.visibilityState (browser tab in background)
 * Either pauses the auto-advance timer; with only one phase the timer is
 * effectively inert (there's nothing to advance to), but the plumbing is
 * preserved so existing keyboard / broadcast control paths still compile.
 *
 * Prev/next stepping comes in via a Supabase Realtime broadcast channel
 * scoped to the event; the stage session may override the handlers (Zitate stepping).
 */

export type CarouselPhase = {
  kind: "clusters";
};

interface UseStageCarouselArgs {
  eventId: string | null;
  questionId: string | null;
  clusterCount: number;
  dbPaused: boolean;
  /** When set, broadcast `next`/`prev` from the console call these instead of
   *  stepping the carousel (used by the Zitate view). */
  onNext?: () => void;
  onPrev?: () => void;
}

const TICK_MS = 100;

function buildPhases(clusterCount: number): CarouselPhase[] {
  // Stage Calm: always one phase if any clusters exist. The grid renders
  // everything on a single screen via ClusterDisplay's 2xN layout.
  if (clusterCount <= 0) return [];
  return [{ kind: "clusters" }];
}

export function useStageCarousel({
  eventId,
  questionId,
  clusterCount,
  dbPaused,
  onNext,
  onPrev,
}: UseStageCarouselArgs): {
  phase: CarouselPhase | null;
  phaseIdx: number;
  totalPhases: number;
  progress: number;
  isPaused: boolean;
  phases: CarouselPhase[];
  advance: () => void;
  retreat: () => void;
} {
  const phases = useMemo(
    () => buildPhases(clusterCount),
    [clusterCount],
  );
  const totalPhases = phases.length;

  const [currentPhaseIdx, setCurrentPhaseIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [docVisible, setDocVisible] = useState(() =>
    typeof document !== "undefined" ? document.visibilityState === "visible" : true,
  );

  const phaseStartTimeRef = useRef<number>(Date.now());
  const totalPhasesRef = useRef(totalPhases);
  totalPhasesRef.current = totalPhases;
  const onNextRef = useRef(onNext);
  const onPrevRef = useRef(onPrev);
  onNextRef.current = onNext;
  onPrevRef.current = onPrev;

  // Reset to phase 0 when the active question changes.
  useEffect(() => {
    setCurrentPhaseIdx(0);
    setProgress(0);
    phaseStartTimeRef.current = Date.now();
  }, [questionId]);

  // Clamp the index if the phase list shrinks below currentPhaseIdx (e.g.,
  // clusters got deleted / re-run reduced count).
  useEffect(() => {
    if (totalPhases === 0) {
      setProgress(0);
      return;
    }
    if (currentPhaseIdx >= totalPhases) {
      setCurrentPhaseIdx(0);
      setProgress(0);
      phaseStartTimeRef.current = Date.now();
    }
  }, [totalPhases, currentPhaseIdx]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisChange = () =>
      setDocVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisChange);
    return () => document.removeEventListener("visibilitychange", onVisChange);
  }, []);

  const effectivelyPaused = dbPaused || !docVisible;
  const currentPhase: CarouselPhase | null =
    totalPhases > 0 && currentPhaseIdx < totalPhases
      ? phases[currentPhaseIdx]
      : null;
  // Stage Calm: only one phase kind remains. The timer is effectively inert
  // (totalPhases is 0 or 1), but we keep the duration computation so the
  // existing tick/progress effect compiles without further restructuring.
  const phaseDurationMs = 1000;

  // Auto-advance + progress tick
  useEffect(() => {
    if (totalPhases === 0 || effectivelyPaused) return;
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - phaseStartTimeRef.current;
      const ratio = phaseDurationMs > 0 ? Math.min(elapsed / phaseDurationMs, 1) : 1;
      setProgress(ratio);
      if (elapsed >= phaseDurationMs) {
        setCurrentPhaseIdx((idx) => (idx + 1) % totalPhasesRef.current);
        phaseStartTimeRef.current = Date.now();
        setProgress(0);
      }
    }, TICK_MS);
    return () => window.clearInterval(interval);
  }, [totalPhases, phaseDurationMs, effectivelyPaused]);

  // Preserve elapsed time across pause toggles so the phase doesn't snap when
  // resumed: shift phaseStartTime forward by the paused duration.
  const pauseStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (effectivelyPaused) {
      pauseStartRef.current = Date.now();
    } else if (pauseStartRef.current !== null) {
      const pausedDuration = Date.now() - pauseStartRef.current;
      phaseStartTimeRef.current += pausedDuration;
      pauseStartRef.current = null;
    }
  }, [effectivelyPaused]);

  // Step the carousel locally. Stable references so consumers can wire these
  // up to keyboard listeners without re-binding on every render. Reads
  // totalPhases through a ref so the closures always see the latest count.
  const advance = useCallback(() => {
    setCurrentPhaseIdx((idx) => {
      const total = totalPhasesRef.current;
      if (total === 0) return 0;
      return (idx + 1) % total;
    });
    phaseStartTimeRef.current = Date.now();
    setProgress(0);
  }, []);

  const retreat = useCallback(() => {
    setCurrentPhaseIdx((idx) => {
      const total = totalPhasesRef.current;
      if (total === 0) return 0;
      return (idx - 1 + total) % total;
    });
    phaseStartTimeRef.current = Date.now();
    setProgress(0);
  }, []);

  // Broadcast channel for prev/next stepping from the facilitator console.
  // Anon-friendly: no DB write. Local arrow-key handlers call advance/retreat
  // directly, not through this channel.
  useEffect(() => {
    if (!eventId) return;
    const channel = supabase
      .channel(`carousel-${eventId}`)
      .on("broadcast", { event: "next" }, () => (onNextRef.current ?? advance)())
      .on("broadcast", { event: "prev" }, () => (onPrevRef.current ?? retreat)())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, advance, retreat]);

  return {
    phase: currentPhase,
    phaseIdx: currentPhaseIdx,
    totalPhases,
    progress,
    isPaused: effectivelyPaused,
    phases,
    advance,
    retreat,
  };
}
